import { normalizeForSearch } from "client/Data/searching";
import {
	useIsMounted,
	useResizeObserver,
	useSynchronizedRef,
	useUnmountedCancellation
} from "client/Hooks";
import {
	convertArbitraryValueForTrackingId,
	generateTrackingId,
	isNotNil,
	isNotNilOrEmpty,
	resolveFormattedTextString,
	setTrackingId,
	debounce
} from "client/Misc/utils";
import {
	ReactElement,
	ReadonlyRefObject,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { ActionListItem } from "../ActionList/ActionListItem";
import { BusyIndicator } from "../BusyIndicator/BusyIndicator";
import { Button } from "../Button/Button";
import { DropOverActionList } from "../DropOverActionList/DropOverActionList";
import { Highlight } from "../HighlightedText/HighlightedText";
import { NoResults } from "../NoResults/NoResults";
import { TagColor } from "../Tag/Tag";
import { KeyEventArguments, TextInput } from "../TextInput/TextInput";
import styles from "./TagSelect.module.less";
import { ValidationError } from "../ValidationError/ValidationError";
import { Key } from "client/Misc/commonTypes";
import { TagList } from "../TagList/TagList";

export type TagModel<TValue> = {
	text: string;
	value: TValue | null;
	color?: TagColor;
	isDisabled?: boolean;
	valueTrackingId?: string;
	highlight?: Highlight | nil;
};

export type SuggestedTagModel<TValue> = Omit<TagModel<TValue>, "value">
	& {
		value: TValue;
	};

export type TagSelectProps<TValue> = {
	value: readonly TagModel<TValue>[];
	suggestedTags: readonly SuggestedTagModel<TValue>[];
	onAdd(newTag: TagModel<TValue>): Promise<boolean>;
	onDelete?(index: number): void;
	onSearch(
		searchTerm: string,
		signal: AbortSignal
	): void;
	trackingId: string;
	isInvalid?: boolean;
	isDisabled?: boolean;
	isBusy?: boolean;
	placeholder?: string;
	searchDebounceInterval?: number;
	onIsSuggestionsBoxOpenChange?(value: boolean): void;
	maxTagLength?: number;
	menuTitle: string;
};

export const TagSelect = <TValue, >(props: TagSelectProps<TValue>): ReactElement => {
	const {
		value,
		suggestedTags,
		onSearch,
		onAdd,
		onDelete,
		isDisabled,
		isInvalid,
		isBusy,
		placeholder,
		trackingId,
		searchDebounceInterval = 0,
		onIsSuggestionsBoxOpenChange,
		maxTagLength,
		menuTitle
	} = props;

	const [isOpen, setIsOpen] = useState<boolean>(false);
	const [searchTerm, setSearchTerm] = useState<string>("");
	const [inputErrorMessage, setInputErrorMessage] = useState<string | undefined>();
	const [hasFocus, setHasFocus] = useState<boolean>(false);

	const ref: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const isMounted: () => boolean = useIsMounted();
	const { width: dropOverWidth } = useResizeObserver(ref);

	const isOpenChangeHandler = useCallback(
		(isOpen: boolean): void => {
			setIsOpen(isOpen);
			onIsSuggestionsBoxOpenChange?.(isOpen);
		},
		[onIsSuggestionsBoxOpenChange]
	);

	const getItemValueTrackingId = useCallback(
		(tag: TagModel<TValue>): string => {
			if (isNotNilOrEmpty(tag.valueTrackingId)) {
				return tag.valueTrackingId;
			}

			return convertArbitraryValueForTrackingId(tag.value);
		},
		[]
	);

	const addHandler = useCallback(
		async (tag?: TagModel<TValue>): Promise<void> => {
			const newTag: TagModel<TValue> = tag ?? {
				text: searchTerm.trim(),
				value: null
			};

			if (newTag.text === "") {
				return;
			}

			try {
				const result: boolean = await onAdd(newTag);

				if (result === true) {
					setSearchTerm("");

					if(isMounted()){
						isOpenChangeHandler(false);
					}
				}
			} catch (error) {
				console.error(error);
			}
		},
		[
			isMounted,
			onAdd,
			searchTerm,
			isOpenChangeHandler
		]
	);

	const keyDownHandler = useCallback(
		(args: KeyEventArguments): boolean => {
			if (args.key === Key.Enter) {
				addHandler();
				return true;
			}

			return false;
		},
		[addHandler]
	);

	const searchHandler = useCallback(
		(searchTerm: string): void => {
			isOpenChangeHandler(true);
			setSearchTerm(searchTerm);
		},
		[isOpenChangeHandler]
	);

	useEffect(
		() => {
			if (
				isNotNil(maxTagLength)
					&& searchTerm.trim().length > maxTagLength
			) {
				const message: string = resolveFormattedTextString({
					template: localize("_TheMaximumLengthIsAmountCharacters"),
					replacements: [
						{
							what: "{{amount}}",
							filler: maxTagLength.toString(10)
						}
					]
				});

				setInputErrorMessage(message);
			} else {
				setInputErrorMessage(undefined);
			}
		},
		[
			maxTagLength,
			searchTerm
		]
	);

	const renderList = (): ReactElement => {
		if (isBusy) {
			return (
				<div className={styles.emptyDataPlaceholder}>
					<BusyIndicator />
				</div>
			);
		}

		if (suggestedTags.length === 0) {
			return (
				<div className={styles.emptyDataPlaceholder}>
					<NoResults
						icon="magnifying-glass"
						title={localize("_NoSuggestions")}
						message={localize("_NoSuggestedInfo")}
					/>
				</div>
			);
		}

		return (
			<>
				{suggestedTags.map((x, index) => (
					<ActionListItem
						key={index}
						text={x.text}
						highlight={x.highlight}
						onClick={
							() => addHandler(
								cleanUpSuggestedTag(x)
							)
						}
						trackingId={generateTrackingId(trackingId, `__suggested-tag|${getItemValueTrackingId(x)}`)}
						tabIndex={-1}
					/>
				))}
			</>
		);
	};

	const debouncedSearchHandler = useMemo(
		(): ((searchTerm: string, signal: AbortSignal) => void) => {
			if (!searchDebounceInterval) {
				return onSearch;
			}
			return debounce(onSearch, searchDebounceInterval);
		},
		[searchDebounceInterval, onSearch]
	);

	useEffect(
		() => {
			debouncedSearchHandler(searchTerm, globalCancellation);
		},
		[
			searchTerm,
			globalCancellation,
			debouncedSearchHandler
		]
	);

	const hasFocusRef: ReadonlyRefObject<boolean> = useSynchronizedRef(hasFocus);

	const blurHandler = useCallback(
		(): void => {
			setHasFocus(false);

			// We cannot directly close the suggestion because shortly after something else in the component
			// can gain focus (i.e. focusout (onBlur) fires first and then focusin (onFocus))
			setTimeout(
				(): void => {
					if (!hasFocusRef.current) {
						isOpenChangeHandler(false);
					}
				},
				0
			);
		},
		[
			hasFocusRef,
			isOpenChangeHandler
		]
	);

	const tagsToRender = useMemo(
		(): readonly TagModel<TValue>[] => value.map(
			x => ({
				...x,
				color: x.color ?? "secondary",
				isDisabled: isDisabled
					? true
					: x.isDisabled
			})
		),
		[
			isDisabled,
			value
		]
	);

	return (
		<>
			<div
				{...setTrackingId(trackingId)}
				ref={ref}
				className={styles.root}
				onFocus={() => setHasFocus(true)}
				onBlur={blurHandler}
			>
				<div className={styles.inputWrapper}>
					<TextInput
						value={searchTerm}
						onChange={searchHandler}
						attachedOn="right"
						placeholder={placeholder}
						isDisabled={isDisabled}
						trackingId={generateTrackingId(trackingId, "__input")}
						onKeyDown={keyDownHandler}
						isInvalid={isInvalid || isNotNil(inputErrorMessage)}
						onFocus={() => isOpenChangeHandler(true)}
					/>
				</div>
				<Button
					kind="secondary"
					text={localize("_Add")}
					attachedOn="left"
					onClick={() => addHandler()}
					isDisabled={isDisabled}
					trackingId="add-new-tag-button"
				/>
				{(isOpen && ref.current) && (
					<DropOverActionList
						trackingId={generateTrackingId(trackingId, "__drop-over")}
						onClose={() => isOpenChangeHandler(false)}
						target={ref.current}
						hasResponsiveDropOver={false}
						width={dropOverWidth}
						isHeightConstrainted={suggestedTags.length > 0}
						outsideClickIgnoreRefs={[ref]}
						menuTitle={menuTitle}
					>
						{renderList()}
					</DropOverActionList>
				)}
			</div>
			<ValidationError
				message={inputErrorMessage}
				trackingId={generateTrackingId(trackingId, "__input-error-message")}
			/>
			{tagsToRender.length > 0 && (
				<div className={styles.tagsContainer}>
					<TagList
						tags={tagsToRender}
						trackingId={generateTrackingId(trackingId, "__added-tags")}
						onTagDelete={!isDisabled
							? onDelete
							: undefined
						}
						responsiveBehavior="wrap"
						areTagsAlreadyPreordered
					/>
				</div>
			)}
		</>
	);
};
TagSelect.displayName = "TagSelect";

export function addHighlightToTags<TValue>(
	items: readonly SuggestedTagModel<TValue>[],
	searchTerm: string
): readonly SuggestedTagModel<TValue>[] {
	const result: SuggestedTagModel<TValue>[] = [];

	for (const item of items) {
		const termIndex: number = normalizeForSearch(item.text)
			.indexOf(
				normalizeForSearch(searchTerm)
			);

		result.push({
			...item,
			highlight: termIndex >= 0
				? [termIndex, searchTerm.length]
				: undefined
		});
	}

	return result;
}

function cleanUpSuggestedTag<T>(suggestedTag: SuggestedTagModel<T>): TagModel<T> {
	const {
		text,
		value,
		color,
		valueTrackingId
	} = suggestedTag;

	return {
		text,
		value,
		color,
		valueTrackingId
	};
}